// Hook de datos de Configuración global, conectado a `loan-config-business`. El catálogo de UI
// (data.mock.ts) pone nombres, orden y rangos; los valores salen del servidor.
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    bulkUpdateParameters,
    listParameters,
    PARAMETERS_QUERY_KEY,
    type BulkUpdateItem,
    type BulkUpdateResult,
    type ParameterResponse,
    type ParametersByTypeResponse,
} from '@/api/services/loan-config/loan-config.client';
import { SYSTEM_PARAMS } from '../data.mock';
import { PARAM_BINDINGS, toUiValue, toWireValue } from '../mapping';
import type { ParamValue, SystemParam } from '../types';

const KEYS = {
    parameters: PARAMETERS_QUERY_KEY,
};

/** Aplana la respuesta agrupada a un índice `paramType#key` → ParameterResponse. */
function indexByTypeAndKey(grouped: ParametersByTypeResponse): Map<string, ParameterResponse> {
    const map = new Map<string, ParameterResponse>();
    for (const list of Object.values(grouped)) {
        for (const p of list) map.set(`${p.paramType}#${p.key}`, p);
    }
    return map;
}

/** Catálogo de UI con valor, default y última modificación del servidor en cada param con binding. */
function mergeParams(grouped: ParametersByTypeResponse): SystemParam[] {
    const byKey = indexByTypeAndKey(grouped);
    return SYSTEM_PARAMS.map((param) => {
        const binding = PARAM_BINDINGS[param.id];
        if (!binding) return param;
        const backend = byKey.get(`${binding.paramType}#${binding.key}`);
        if (!backend) return param;
        return {
            ...param,
            value: toUiValue(binding, backend.effectiveValue),
            defaultValue: toUiValue(binding, backend.defaultValue),
            lastModified: (backend.updatedAt || '').slice(0, 10),
            lastModifiedBy: backend.lastModifiedByUserId,
        };
    });
}

/** El `updatedAt` más reciente de todos los parámetros; `null` sin datos. */
export function latestUpdatedAt(grouped: ParametersByTypeResponse): string | null {
    let latest: string | null = null;
    for (const list of Object.values(grouped)) {
        for (const p of list) {
            if (p.updatedAt && (latest === null || p.updatedAt > latest)) latest = p.updatedAt;
        }
    }
    return latest;
}

export function useSystemParams(enabled: boolean): {
    params: SystemParam[];
    lastUpdatedAt: string | null;
    isMock: boolean;
    isLoading: boolean;
} {
    const query = useQuery({
        queryKey: KEYS.parameters,
        queryFn: listParameters,
        staleTime: 60_000,
        enabled,
    });

    const params = useMemo(
        () => (query.data ? mergeParams(query.data) : SYSTEM_PARAMS),
        [query.data],
    );
    const lastUpdatedAt = useMemo(
        () => (query.data ? latestUpdatedAt(query.data) : null),
        [query.data],
    );

    return {
        params,
        lastUpdatedAt,
        isMock: !query.isSuccess,
        isLoading: query.isLoading,
    };
}

export interface SaveSystemParamsInput {
    edits: { id: string; value: ParamValue }[];
    reason: string;
}

/** Guardado de las secciones 1-5: convierte cada edición al wire y copia el motivo a cada ítem. */
export function useSaveSystemParams() {
    const qc = useQueryClient();
    return useMutation<BulkUpdateResult, Error, SaveSystemParamsInput>({
        mutationFn: ({ edits, reason }) => {
            const parameters: BulkUpdateItem[] = edits.flatMap(({ id, value }) => {
                const binding = PARAM_BINDINGS[id];
                if (!binding) return [];
                return [
                    {
                        paramType: binding.paramType,
                        key: binding.key,
                        value: toWireValue(binding, value),
                        reason,
                    },
                ];
            });
            return bulkUpdateParameters(parameters);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.parameters }),
    });
}
